// 親ツールパネルに対する子パネルの配置を解決する純粋関数群。
// 親の座標・寸法は入力としてのみ扱い、この関数が親を動かすことはない。
// 子は「親のどちら側に親自身が出ているか（side）」に応じて端をそろえ、
// 上下どちらへ展開するかと、その展開側の空きに収まる上限寸法だけを決める。

/** 親と子の間隔。既存のフローティング UI と同じ 8px を維持する。 */
export const TOOLBAR_CHILD_GAP_PX = 8;

/** 親パネルが基準（選択図形・メインツールバー）のどちら側に出ているか。 */
export type ToolbarParentSide = "top" | "bottom" | "left" | "right";

/** 子を親の上下どちらへ展開したか。 */
export type ToolbarChildDirection = "above" | "below";

export interface ToolbarRect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

export interface ToolbarSize {
  readonly width: number;
  readonly height: number;
}

export interface ToolbarChildPlacementInput {
  /** ビューポート座標での親パネルの実矩形。 */
  readonly parent: ToolbarRect;
  /** 上限を適用していない状態での子の自然寸法。 */
  readonly child: ToolbarSize;
  /** ビューポート座標での安全領域。 */
  readonly safeArea: ToolbarRect;
  readonly side: ToolbarParentSide;
  /** 子の先頭操作行と surface の block 方向余白を含む、最低限使える高さ。 */
  readonly minimumUsableHeight: number;
}

export type ToolbarChildPlacement =
  | {
      /** 上下どちらにも最低限の高さを確保できず、開操作を成立させられない。 */
      readonly open: false;
    }
  | {
      readonly open: true;
      readonly direction: ToolbarChildDirection;
      /** ビューポート座標。 */
      readonly left: number;
      readonly top: number;
      readonly maxWidth: number;
      readonly maxHeight: number;
    };

function clamp(value: number, lower: number, upper: number): number {
  return Math.min(Math.max(value, lower), upper);
}

/** 表 §9.5 の優先展開方向。上側に出ている親だけ上へ、それ以外は親の下を優先する。 */
export function preferredChildDirection(side: ToolbarParentSide): ToolbarChildDirection {
  return side === "top" ? "above" : "below";
}

export function resolveToolbarChildPlacement(
  input: ToolbarChildPlacementInput
): ToolbarChildPlacement {
  const safeRight = input.safeArea.left + input.safeArea.width;
  const safeBottom = input.safeArea.top + input.safeArea.height;
  const parentRight = input.parent.left + input.parent.width;
  const parentBottom = input.parent.top + input.parent.height;

  // 展開側ごとの利用可能高。親へ重ならないよう、間隔ぶんを先に差し引く。
  const available: Record<ToolbarChildDirection, number> = {
    above: Math.max(0, input.parent.top - TOOLBAR_CHILD_GAP_PX - input.safeArea.top),
    below: Math.max(0, safeBottom - parentBottom - TOOLBAR_CHILD_GAP_PX),
  };
  const preferred = preferredChildDirection(input.side);
  const opposite: ToolbarChildDirection = preferred === "above" ? "below" : "above";

  let direction: ToolbarChildDirection;
  if (input.child.height <= available[preferred]) {
    direction = preferred;
  } else if (input.child.height <= available[opposite]) {
    // 優先側へ自然高が収まらず、反対側へ収まる場合だけ子を反転する。親は動かさない。
    direction = opposite;
  } else {
    // どちらにも自然高が収まらない場合は、最低限使える高さを確保できる側のうち広い側を選ぶ。
    const usable = ([preferred, opposite] as const).filter(
      (candidate) => available[candidate] >= input.minimumUsableHeight
    );
    // 候補（0〜2 件）から利用可能高が最大の側を 1 つ選ぶ。同値では優先側を維持する。
    const best = usable.reduce<ToolbarChildDirection | null>(
      (current, candidate) =>
        current === null || available[candidate] > available[current] ? candidate : current,
      null
    );
    if (best === null) return { open: false };
    direction = best;
  }

  const maxHeight = available[direction];
  const maxWidth = Math.max(0, input.safeArea.width);
  const height = Math.min(input.child.height, maxHeight);
  const width = Math.min(input.child.width, maxWidth);

  // 表示側ごとの水平基準。左に出た親は右端、右に出た親は左端、上下は水平中央でそろえる。
  const alignedLeft =
    input.side === "left"
      ? parentRight - width
      : input.side === "right"
        ? input.parent.left
        : input.parent.left + input.parent.width / 2 - width / 2;
  const maxLeft = Math.max(input.safeArea.left, safeRight - width);
  const left = clamp(alignedLeft, input.safeArea.left, maxLeft);
  const top =
    direction === "above"
      ? input.parent.top - TOOLBAR_CHILD_GAP_PX - height
      : parentBottom + TOOLBAR_CHILD_GAP_PX;

  return { open: true, direction, left, top, maxWidth, maxHeight };
}

/**
 * ビューポート座標の配置結果を、親子ラッパーを原点とする相対座標へ変換する。
 * 子は通常フロー外の overlay として親子ラッパーへ絶対配置するため、描画にはこちらを使う。
 */
export function toRelativeToolbarChildPlacement(
  placement: ToolbarChildPlacement,
  origin: { readonly left: number; readonly top: number }
): ToolbarChildPlacement {
  if (!placement.open) return placement;
  return {
    ...placement,
    left: placement.left - origin.left,
    top: placement.top - origin.top,
  };
}

/** 2 つの配置結果が全項目で等しいか。state の同値更新と再計測ループを止めるために使う。 */
export function toolbarChildPlacementsEqual(
  previous: ToolbarChildPlacement | null,
  next: ToolbarChildPlacement | null
): boolean {
  if (previous === null || next === null) return previous === next;
  if (!previous.open || !next.open) return previous.open === next.open;
  return (
    previous.direction === next.direction &&
    previous.left === next.left &&
    previous.top === next.top &&
    previous.maxWidth === next.maxWidth &&
    previous.maxHeight === next.maxHeight
  );
}
